import { Component, Prop, State, h, Watch } from '@stencil/core';
import warn from '../../capabilities/warn';

/**
 * @description Icon component.  Places inline SVGs into the page.
 * @part icon - allows styling of the svg
 * @use
 * @ssh
 * @since 5.37.2
 * @version 3
 */
@Component({
  tag: 'hp6-icon',
  styleUrl: 'icon.scss',
  shadow: true,
})
export class Icon {
  /**
   * Name of the icon
   */
  @Prop({ reflect: true }) name!: string;

  /**
   * Color of first path
   */
  @Prop() pathColorOneSelection: 'color-primary-base' | 'color-secondary-light';

  /**
   * Color of second path
   */
  @Prop() pathColorTwoSelection: 'color-primary-base' | 'color-secondary-light';

  /**
   * Size of the icon for all sizes. (same as size-xs).
   *
   * TODO: this prop needs to be named sizeSelection to be congruent with documented standards
   * The same consideration could also be made for the rest of the this.size* props
   *
   */
  @Prop({ reflect: true }) sizeSelection: 'xxs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' = 'sm';

  /**
   * Size of the icon at xsmall breakpoint and up
   */
  @Prop() sizeXsSelection: 'xxs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl';

  /**
   * Size of the icon at small breakpoint and up
   */
  @Prop() sizeSmSelection: 'xxs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl';

  /**
   * Size of the icon at medium breakpoint and up
   */
  @Prop() sizeMdSelection: 'xxs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl';

  /**
   * Size of the icon at large breakpoint
   */
  @Prop() sizeLgSelection: 'xxs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl';

  /**
   * Used for accessibility to describe icon
   */
  @Prop() description: string = '';

  @State() iconSet: 'brand' | 'system';

  componentWillLoad() {
    this.setIconSet(this.name, null);
  }

  @Watch('name')
  setIconSet(newName, oldName) {
    if (newName && newName !== oldName) {
      const [iconSet] = newName.split('-');
      if (iconSet && (iconSet === 'brand' || iconSet === 'system')) {
        this.iconSet = iconSet;
      } else {
        warn(`icon set ${newName} is not valid`);
      }
    }
  }

  render() {
    const { name, description, iconSet, pathColorOneSelection, pathColorTwoSelection } = this;

    if (!iconSet) {
      return null;
    }

    let classSet = 'icon';
    if (pathColorOneSelection) {
      classSet += ` path-one-${pathColorOneSelection}`;
    }
    if (pathColorTwoSelection) {
      classSet += ` path-two-${pathColorTwoSelection}`;
    }

    // with shadow DOM screen-reader-only will not work.
    // TODO: invesigate https://www.npmjs.com/package/element-internals-polyfill
    // add aria-description attribute instead
    return (
      <div aria-hidden={description ? 'false' : 'true'}>
        {description && <hp6-screen-reader-only>{description}</hp6-screen-reader-only>}
        {iconSet === 'system' ? (
          <hp6-system-icon name={name} class="icon" part="icon" />
        ) : (
          <hp6-brand-icon name={name} part="icon" class={classSet} />
        )}
      </div>
    );
  }
}
